import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';

export type MatrixLevel = 'Low' | 'Medium' | 'High';

export interface MatrixItem {
  itemKey: string;
  level: MatrixLevel;
}

export interface MatrixArea {
  areaKey: string;
  areaLevel: MatrixLevel;
  items: [MatrixItem, MatrixItem];
}

export type MeetingMatrix = [MatrixArea, MatrixArea, MatrixArea];

/** Legacy (v2.1) report: produced before the ReCap v3 engine, kept as generated. */
export interface RecordingAnalysis {
  status: string;
  titleLine: string;
  summaryLine: string;
  body: string[];
  question: string;
  matrix?: MeetingMatrix;
  statusMatrix?: string;
}

// ReCap v3 report — mirrors RecapReport in terraform/lambda/teamei/types.ts.
// Language-neutral: every text is a `recap.*` locale key, rendered by RecapReportView.
export type IndicatorLevel = 'high' | 'medium' | 'low' | 'na';
export type RecapStatus = 'solido' | 'da_approfondire' | 'medio' | 'rischio' | 'na';
export type MeetingPurpose = 'decisional' | 'exploratory' | 'informative' | 'other' | 'unspecified';
export const MEETING_PURPOSES: MeetingPurpose[] = [
  'unspecified',
  'decisional',
  'exploratory',
  'informative',
  'other',
];

export interface RecapWarning {
  id: 'W01' | 'W02' | 'W03' | 'W04' | 'W05' | 'W06';
  level: 'priority' | 'attention' | 'operational';
  main: boolean;
  withConcentration?: boolean;
}

export interface RecapReport {
  version: 3;
  status: RecapStatus;
  /** Legacy (engine 3.0–3.1) only: newer reports use the "da_approfondire" status */
  qualifier?: 'attention';
  quality: 'ok' | 'low';
  purpose: MeetingPurpose;
  indicators: { trust: IndicatorLevel; safety: IndicatorLevel; belonging: IndicatorLevel };
  warnings: RecapWarning[];
  readingKeys: string[][];
  finalKeys: string[];
  versions: { analysis: string; model: string; prompt: string; rules: string };
  generatedAt: string;
}

export interface Recording {
  id: string;
  transcription: string;
  /** Current (v3) report; null on legacy records not yet re-analysed */
  recap?: RecapReport | null;
  /** True when `recap` was produced by an earlier v3 engine version */
  recapOutdated?: boolean;
  /** Status of a manual re-analysis with the v3 engine */
  recapStatus?: 'pending' | 'done' | 'error';
  /** Legacy (v2.1) report, served as stored */
  analysis: RecordingAnalysis | null;
  /** Language the legacy report is in (may differ from the UI language) */
  analysisLang?: string;
  processingStatus?: 'pending' | 'done' | 'error';
  uploadedAt?: string;
  owner?: string;
}

interface TableRecordingsRowProps {
  row: Recording;
  setAnalysisModalOpen: (recording: Recording | null) => void;
  showOwner?: boolean;
}

export const TableRecordingsRow: React.FC<TableRecordingsRowProps> = ({
  row,
  setAnalysisModalOpen,
  showOwner,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const openRow = (openState: boolean) => {
    setOpen(openState);
  };
  return (
    <React.Fragment>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }} hover>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => {
              openRow(!open);
            }}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell scope="row">{row.id}</TableCell>
        {showOwner && <TableCell>{row.owner}</TableCell>}
        <TableCell>
          <Button
            variant="outlined"
            onClick={() => {
              setAnalysisModalOpen(row);
            }}
          >
            {t('recordings.show_analysis')}
          </Button>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <TableRow key={row.id}>
                <TableCell sx={{ borderBottom: 0 }} scope="row">
                  {JSON.stringify(row.transcription)}
                </TableCell>
              </TableRow>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};
